import { exitingGate, occupiedKeysExcept, powerWithMover, slideAndExitAgainst } from './free-move';
/**
 * Apply one solver move: slide the block until a wall, another block, or a gate
 * it can leave through stops it — whichever comes first — and remove it if it was
 * the gate. Returns `null` when the move is not legal (no such block, or it
 * cannot move that way) — callers decide whether that is a bug or a user mistake.
 *
 * WP-50.4: this used to slide the block ALL the way and only then ask about the
 * gate, which is not how the board resolves a drag. It now defers the whole
 * question to `slideAndExitAgainst`, the one place the two rules are composed.
 */
export function applyFreeMove(blocks, move, exits, gridSize, opts = {}, plates) {
    const block = blocks.find((b) => b.id === move.blockId);
    if (!block)
        return null;
    const others = occupiedKeysExcept(blocks, block.id);
    const outcome = slideAndExitAgainst(block, move.direction, others, exits, gridSize, opts, plates);
    if (!outcome)
        return null;
    const { moved, exited } = outcome;
    const next = exited
        ? blocks.filter((b) => b.id !== block.id)
        : blocks.map((b) => (b.id === block.id ? moved : b));
    return {
        blocks: next,
        moved,
        exitedId: exited ? block.id : null,
        exitedGate: exited ?? null,
        powered: plates
            ? powerWithMover(moved, others, plates)
            : (opts.poweredPlateIds ?? new Set()),
    };
}
/**
 * Materialise every frame of a solution. Frame 0 is the board as handed in,
 * minus any block that already sits at its gate — the same normalisation the
 * solver does before it starts, so frame 0 matches the position the move list
 * was computed from.
 *
 * Module 75 — pass the board's `plates` on a powered board. Power is then
 * judged at every position the way the solver judges it; without them the
 * replay holds the start-of-replay `opts.poweredPlateIds` for every move.
 */
export function replayFreeSolution(blocks, moves, exits, gridSize, opts = {}, plates) {
    let current = blocks.filter((b) => {
        const others = occupiedKeysExcept([...blocks], b.id);
        const here = plates ? { ...opts, poweredPlateIds: powerWithMover(b, others, plates) } : opts;
        return exitingGate(b, exits, gridSize, others, here) === null;
    });
    const frames = [
        { index: 0, blocks: current, move: null, exitedId: null, remaining: current.length },
    ];
    for (let i = 0; i < moves.length; i++) {
        const result = applyFreeMove(current, moves[i], exits, gridSize, opts, plates);
        if (!result)
            return { frames, cleared: current.length === 0, invalidAt: i + 1 };
        current = result.blocks;
        frames.push({
            index: i + 1,
            blocks: current,
            move: moves[i],
            exitedId: result.exitedId,
            remaining: current.length,
        });
    }
    return { frames, cleared: current.length === 0, invalidAt: null };
}
